import React, { CSSProperties } from 'react'

import classNames from 'classnames'

import VisuallyHidden from 'hsds/components/visually-hidden'
import { getValidProps } from 'hsds/utils/react'

import { getSize, legacySizes } from './Spinner.utils'

import { SpinnerCircleUI, SpinnerSVGUI, SpinnerUI } from './Spinner.styles'

type Props = {
  /** Custom class names to be added to the component. */
  className?: string
  /** Color of the spinner. */
  color?: string
  /** Rounds the stroke ends of the spinner SVG. */
  isRounded?: boolean
  /** Determines the size of the spinner. */
  size?:
    | number
    | keyof typeof legacySizes
    | `${(typeof legacySizes)[keyof typeof legacySizes]}`
  /** Speed of the spinning animation (in `ms`). */
  speed?: number
  /** Data attr for Cypress tests. */
  'data-cy'?: string
  shade?: string
}

export function Spinner({
  'data-cy': dataCy = 'Spinner',
  color = 'currentColor',
  isRounded = true,
  size = 16,
  speed = 1400,
  className,
  ...rest
}: Props) {
  return (
    <SpinnerUI
      {...getValidProps(rest)}
      aria-busy
      data-cy={dataCy}
      className={classNames('c-Spinner', className)}
      style={
        {
          '--hsds-spinner-size': size !== 16 && `${getSize(size)}px`,
        } as CSSProperties
      }
    >
      <SpinnerSVGUI
        className="c-SpinnerSVG"
        viewBox="22 22 44 44"
        style={
          {
            '--hsds-spinner-speed': speed !== 1400 && `${speed}ms`,
          } as CSSProperties
        }
      >
        <SpinnerCircleUI
          className={classNames('c-SpinnerCircle', isRounded && 'is-rounded')}
          cx={44}
          cy={44}
          r={20.2}
          style={
            {
              '--hsds-spinner-circle-stroke': color !== 'currentColor' && color,
            } as CSSProperties
          }
        />
      </SpinnerSVGUI>
      <VisuallyHidden>Loading</VisuallyHidden>
    </SpinnerUI>
  )
}

export default Spinner
